/**
 * /alerts — refonte « bonnes affaires » du 2026-09-14.
 *
 * Mêmes profils et même formulaire qu'avant ; le « bénéfice minimum » n'est
 * proposé qu'aux revendeurs, et chaque alerte mène à son annonce.
 *
 * 2026-10-22 : plus d'abonnement. L'envoi des alertes ne vise encore que les
 * anciens abonnés (backend services/scheduler) : les autres comptes voient
 * « arrivent bientôt », sans prix ni offre, en attendant cette décision.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import { useAuth } from '../context/AuthContext';
import { hasResellerPlan } from '../utils/profileAccess';
import api from '../services/api';
import './ComptePages.css';

const TYPES = {
  new_listing: 'Nouvelle bonne affaire',
  price_drop: 'Baisse de prix',
  negotiation_complete: 'Accord conclu',
  fraud_detected: 'Vérification requise',
};

const CHAMPS = [
  { key: 'name', label: 'Nom du profil', placeholder: 'Mon profil', required: true },
  { key: 'budget_min', label: 'Budget min (€)', placeholder: '0', type: 'number' },
  { key: 'budget_max', label: 'Budget max (€)', placeholder: '15000', type: 'number' },
  { key: 'brands', label: 'Marques', placeholder: 'Renault, Peugeot' },
  { key: 'models_list', label: 'Modèles', placeholder: 'Clio, 208' },
  { key: 'year_min', label: 'Année min', placeholder: '2015', type: 'number' },
  { key: 'year_max', label: 'Année max', placeholder: '2023', type: 'number' },
  { key: 'mileage_max', label: 'Kilométrage max', placeholder: '100000', type: 'number' },
  { key: 'locations', label: 'Zone géographique', placeholder: 'Paris, Lyon, 33' },
  { key: 'fuel_types', label: 'Énergies', placeholder: 'Essence, Diesel' },
  { key: 'driver_age', label: 'Âge du conducteur', placeholder: '22', type: 'number' },
  { key: 'min_profit_margin', label: 'Marge minimum (€)', placeholder: '500', type: 'number', revendeur: true },
];

const VIDE = {
  name: 'Mon profil', budget_min: '', budget_max: '', brands: '', models_list: '', year_min: '', year_max: '',
  mileage_max: '', locations: '', fuel_types: '', driver_age: '', min_profit_margin: '',
};

const liste = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
const nombreOuNul = (v) => (v ? Number(v) : null);

export default function AlertsPage() {
  const { user } = useAuth();
  const [alertes, setAlertes] = useState([]);
  const [profils, setProfils] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [formulaire, setFormulaire] = useState(false);
  const [valeurs, setValeurs] = useState(VIDE);
  const [enregistrement, setEnregistrement] = useState(false);
  const [message, setMessage] = useState(null);

  const revendeur = hasResellerPlan(user);
  const abonne = user?.is_admin || ['PARTICULIERPASS', 'REVENDEURPASS', 'PROPASS', 'GARAGEPASS', 'ULTRAPASS', 'JEUNEPASS'].includes(user?.subscription_plan);

  useEffect(() => {
    Promise.all([
      api.get('/api/alerts/').then((r) => setAlertes(r.data || [])).catch(() => {}),
      api.get('/api/alerts/profiles').then((r) => setProfils(r.data || [])).catch(() => {}),
    ]).finally(() => setChargement(false));
  }, []);

  async function enregistrer(e) {
    e.preventDefault();
    setEnregistrement(true);
    setMessage(null);
    try {
      const res = await api.post('/api/alerts/profiles', {
        name: valeurs.name,
        budget_min: nombreOuNul(valeurs.budget_min),
        budget_max: nombreOuNul(valeurs.budget_max),
        brands: liste(valeurs.brands),
        models_list: liste(valeurs.models_list),
        year_min: nombreOuNul(valeurs.year_min),
        year_max: nombreOuNul(valeurs.year_max),
        mileage_max: nombreOuNul(valeurs.mileage_max),
        locations: liste(valeurs.locations),
        fuel_types: liste(valeurs.fuel_types),
        driver_age: nombreOuNul(valeurs.driver_age),
        min_profit_margin: revendeur ? nombreOuNul(valeurs.min_profit_margin) : null,
      });
      setProfils((p) => [...p, res.data]);
      setFormulaire(false);
      setValeurs(VIDE);
      setMessage({ ok: true, texte: 'Profil créé : vous serez prévenu des affaires qui y correspondent.' });
    } catch (err) {
      setMessage({ ok: false, texte: err.response?.data?.detail || 'Le profil n’a pas pu être créé. Réessayez.' });
    } finally {
      setEnregistrement(false);
    }
  }

  async function supprimer(id) {
    await api.delete(`/api/alerts/profiles/${id}`).catch(() => {});
    setProfils((p) => p.filter((x) => x.id !== id));
  }

  return (
    <Page
      titre="Alertes"
      chapeau="Soyez prévenu dès qu’une bonne affaire correspond à ce que vous cherchez."
    >
      <section className="zk-cpt-section" style={{ marginTop: 0 }}>
        <div className="zk-cpt-section-tete">
          <h2>Mes profils de recherche</h2>
          {abonne && (
            <button type="button" className={`zk-bouton${formulaire ? ' second' : ''}`} onClick={() => setFormulaire((f) => !f)}>
              {formulaire ? 'Annuler' : 'Ajouter un profil'}
            </button>
          )}
        </div>

        {!abonne && (
          <div className="zk-carte zk-cpt-vide">
            <h2>Les alertes personnalisées arrivent bientôt</h2>
            <p>
              Marque, modèle, budget, kilométrage et zone : vous serez prévenu dès qu’une affaire correspondante
              apparaît. En attendant, toutes les bonnes affaires sont sur la page dédiée.
            </p>
            <Link to="/explore" className="zk-bouton">Voir les bonnes affaires</Link>
          </div>
        )}

        {abonne && message && (
          <div className={`zk-alerte ${message.ok ? 'ok' : 'erreur'}`} role="status" style={{ marginBottom: 12 }}>{message.texte}</div>
        )}

        {abonne && formulaire && (
          <form onSubmit={enregistrer} className="zk-carte" style={{ marginBottom: 14 }}>
            <div className="zk-cpt-form-grille">
              {CHAMPS.filter((c) => !c.revendeur || revendeur).map((c) => (
                <div key={c.key} className="zk-champ">
                  <label htmlFor={`alerte-${c.key}`}>{c.label}</label>
                  <input
                    id={`alerte-${c.key}`} type={c.type || 'text'} placeholder={c.placeholder} required={!!c.required}
                    value={valeurs[c.key]} onChange={(e) => setValeurs((v) => ({ ...v, [c.key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <button type="submit" className="zk-bouton" disabled={enregistrement}>
              {enregistrement ? 'Enregistrement…' : 'Enregistrer le profil'}
            </button>
          </form>
        )}

        {abonne && !chargement && profils.length === 0 && !formulaire && (
          <p className="zk-carte" style={{ color: 'var(--zk-muted)', margin: 0 }}>
            Aucun profil pour l’instant : créez-en un pour recevoir vos premières alertes.
          </p>
        )}

        {abonne && profils.length > 0 && (
          <div className="zk-cpt-profils">
            {profils.map((p) => (
              <div key={p.id} className="zk-carte zk-cpt-profil">
                <div className="zk-cpt-profil-tete">
                  {p.name}
                  <span className={`zk-cpt-etat${p.is_active ? '' : ' off'}`}>{p.is_active ? 'Actif' : 'Inactif'}</span>
                </div>
                <div className="zk-cpt-criteres">
                  {p.budget_max && <span>≤ {p.budget_max.toLocaleString('fr-FR')} €</span>}
                  {p.brands?.length > 0 && <span>{p.brands.join(', ')}</span>}
                  {p.locations?.length > 0 && <span>{p.locations.join(', ')}</span>}
                  {p.mileage_max && <span>≤ {p.mileage_max.toLocaleString('fr-FR')} km</span>}
                </div>
                <button type="button" className="zk-cpt-supprimer" onClick={() => supprimer(p.id)}>Supprimer</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="zk-cpt-section">
        <div className="zk-cpt-section-tete"><h2>Alertes reçues</h2></div>
        {alertes.length === 0 ? (
          <p className="zk-carte" style={{ color: 'var(--zk-muted)', margin: 0 }}>Aucune alerte reçue pour l’instant.</p>
        ) : (
          <ul className="zk-cpt-alertes">
            {alertes.map((a) => (
              <li key={a.id} className="zk-carte zk-cpt-alerte">
                <div>
                  <b>{TYPES[a.alert_type] || a.alert_type}</b>
                  <span>{new Date(a.created_at).toLocaleString('fr-FR')}{a.is_sent ? ' · envoyée' : ''}</span>
                </div>
                {a.listing_id && <Link to={`/listings/${a.listing_id}`} className="zk-lien">Voir l’annonce →</Link>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <BandeauAffaires titre="Les affaires que vos alertes surveillent" />
    </Page>
  );
}
